import React, { useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';

// Actions
import {
  listEmployeesRequest,
  filterEmployeesRequest,
} from 'store/ducks/employees/actions';
import { setSelectedEmployees } from 'store/ducks/reports/actions';

// Types
import { EmployeeInterface } from 'store/ducks/employees/types';
import { ApplicationState } from 'store';

// Helpers
import { useDebouncedSearch } from 'helpers/debounceHook';
import { promiseDispatch } from 'store/utils/promiseAction';
import {
  getCurrentSearchTerm,
  setCurrentSearchTerm,
} from 'helpers/tableFilters';

// Components
import { SearchAsyncComponent } from 'components/SearchAsyncComponent';

// Styles
import useStyles from './DriverNavigation.styles';

const storegeLimit =
  JSON.parse(window.localStorage.getItem('persist:root') as string)
    ?.localLimit || '10';
const { localLimit } = JSON.parse(storegeLimit) || 10;

async function searchDriver(filter: string) {
  if (getCurrentSearchTerm() && filter.trim()) {
    return promiseDispatch(
      filterEmployeesRequest({
        body: {
          name: `%${filter}%`,
          _limit: localLimit,
        },
      }),
    );
  }
  return false;
}

const useSearchDriver = () => useDebouncedSearch(searchDriver);

const DriverNavigation: React.FC = () => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [employees, setEmployees] = useState<EmployeeInterface[]>([]);
  const [
    autoCompleteText,
    setAutoCompleteText,
    searchResults,
  ] = useSearchDriver();

  const filteredEmployees = useSelector(
    (state: ApplicationState) => state.reports.selectedEmployees,
  );

  const employeesList = useSelector(
    (state: ApplicationState) => state.employees.employeesList,
  );

  useEffect(() => {
    dispatch(listEmployeesRequest({}));
  }, [dispatch]);

  useEffect(() => {
    if (employeesList.length > 0) {
      setEmployees(employeesList);
    }
  }, [employeesList]);

  const handleEmployee = (employee: EmployeeInterface) => {
    const updated = [...filteredEmployees];
    const index = filteredEmployees.findIndex((e) => e.id === employee.id);

    if (index === -1) updated.push({ id: employee.id, name: employee.name });
    else updated.splice(index, 1);

    dispatch(
      setSelectedEmployees({
        selectedEmployees: updated,
      }),
    );
  };

  return (
    <Box>
      <SearchAsyncComponent
        placeholder='Digite o nome do motorista'
        loading={searchResults.loading}
        classes={{
          root: classes.root,
        }}
        inputSize='small'
        onChange={(text: string) => {
          setCurrentSearchTerm(text);
          setAutoCompleteText(text);
          if (!text) {
            dispatch(
              listEmployeesRequest({
                filtering: true,
              }),
            );
          }
        }}
        value={autoCompleteText}
      />
      <Box marginTop={1}>
        <List dense>
          {Array.isArray(employees) &&
            employees.map((item: EmployeeInterface) => (
              <ListItem key={item.id} button>
                <ListItemText
                  id={item.id}
                  primary={item.name}
                  onClick={() => handleEmployee(item)}
                  classes={{ root: classes.itemText }}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge='end'
                    color='primary'
                    onChange={() => handleEmployee(item)}
                    checked={!!filteredEmployees.find((e) => e.id === item.id)}
                    inputProps={{ 'aria-labelledby': item.id }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  );
};

export { DriverNavigation };
export default DriverNavigation;
